import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import DeleteServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/DeleteServicePlanFormItemModal';
import UpdateServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/UpdateServicePlanItemFormModal';
import { Link } from 'react-router-dom';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

interface CellServicePlanItemActionsProps extends CellContext<ServicePlanItem, unknown> {
    isServicePlanActive?: boolean;
}

export default function CellServicePlanItemActions({ row, isServicePlanActive }: CellServicePlanItemActionsProps) {
    const { formatMessage } = useIntl();
    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link to={`/service-plan-item/${row.original.id}`}>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>
                {permissionManageServicePlans && !isServicePlanActive && (
                    <>
                        <Tooltip>
                            <UpdateServicePlanItemFormModal
                                semantics={{ entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase() }}
                                selectedServicePlanItem={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateServicePlanItemFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteServicePlanItemFormModal selectedServicePlanItem={row.original}>
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteServicePlanItemFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
}
